import type { SchoolSyncStatistics } from 'dashboard/types';

const isTeacherRole = function (currentRole: string) {
  return currentRole === 'teacher';
};

// returns the total number of steps in the current account review flow
export const totalSteps = function (
  currentRole: string,
  schoolSyncStats: SchoolSyncStatistics
) {
  if (isTeacherRole(currentRole)) {
    if (
      hasExistingAccounts(currentRole, schoolSyncStats) &&
      hasNewAccounts(currentRole, schoolSyncStats)
    ) {
      // if there are both existing and new accounts, a teacher has 3 total steps
      return 3;
    } else {
      // with *either* existing or new accounts, a teacher has 2 total steps
      return 2;
    }
  }

  // (for students:)
  if (
    hasExistingAccounts(currentRole, schoolSyncStats) &&
    hasNewAccounts(currentRole, schoolSyncStats)
  ) {
    /* a student has one extra step - UNLESS there are no selected new accounts -
       then we skip that step entirely and there are still 3 😅 */
    if (schoolSyncStats.totalNewStudentsWithCreateNewRecord === 0) return 3;

    return 4;
  } else {
    // as above, student has one extra step unless there are no selected new accounts
    if (schoolSyncStats.totalNewStudentsWithCreateNewRecord === 0) return 2;

    return 3;
  }
};

// returns true if the current step is the existing accounts step
export const isExistingAccountsStep = function (
  currentRole: string,
  currentStep: number,
  schoolSyncStats: SchoolSyncStatistics
) {
  /* the first step is the existing accounts for students and teachers, as long
     as existing accounts exist for the current school */
  return currentStep === 1 && hasExistingAccounts(currentRole, schoolSyncStats);
};

// returns true if the current step is the new accounts step
export const isNewAccountsStep = function (
  currentRole: string,
  currentStep: number,
  schoolSyncStats: SchoolSyncStatistics
) {
  if (hasExistingAccounts(currentRole, schoolSyncStats)) {
    if (hasNewAccounts(currentRole, schoolSyncStats)) {
      // with existing and new accounts, new accounts has to be step 2
      return currentStep === 2;
    } else {
      // there are NO new accounts in this flow
      return false;
    }
  } else {
    // without existing accounts, new accounts has to be the first step
    return currentStep === 1;
  }
};

// returns true if the current step is the account access step (only for students)
export const isAccountAccessStep = function (
  currentStep: number,
  schoolSyncStats: SchoolSyncStatistics
) {
  // if no new accounts are set to create, this step never displays
  if (schoolSyncStats.totalNewStudentsWithCreateNewRecord === 0) return false;

  if (
    hasExistingAccounts('student', schoolSyncStats) &&
    hasNewAccounts('student', schoolSyncStats)
  ) {
    // with existing and new accounts, account access is step 3 (after each)
    return currentStep === 3;
  }

  // otherwise default to step 2
  return currentStep === 2;
};

// returns true if the current step is the summary step
export const isSummaryStep = function (
  currentRole: string,
  currentStep: number,
  schoolSyncStats: SchoolSyncStatistics
) {
  if (isTeacherRole(currentRole)) {
    if (
      hasExistingAccounts(currentRole, schoolSyncStats) &&
      hasNewAccounts(currentRole, schoolSyncStats)
    ) {
      // teacher summary is step 3 with new and existing
      return currentStep === 3;
    } else {
      // otherwise (with new OR existing) it's step 2
      return currentStep === 2;
    }
  }

  // (for students:)

  // if no new accounts are set to create, student summary is a step earlier
  if (schoolSyncStats.totalNewStudentsWithCreateNewRecord === 0) {
    if (
      hasExistingAccounts(currentRole, schoolSyncStats) &&
      hasNewAccounts(currentRole, schoolSyncStats)
    ) {
      // student summary is step 3 with new and existing
      return currentStep === 3;
    } else {
      // otherwise (with new OR existing) it's step 2
      return currentStep === 2;
    }
  }

  if (
    hasExistingAccounts(currentRole, schoolSyncStats) &&
    hasNewAccounts(currentRole, schoolSyncStats)
  ) {
    // student summary is step 4 with new and existing
    return currentStep === 4;
  } else {
    // otherwise (with new OR existing) it's step 3
    return currentStep === 3;
  }
};

// returns the users string to be *excluded* from the current step
export const excludeUsersString = function (
  currentRole: string,
  currentStep: number,
  schoolSyncStats: SchoolSyncStatistics
) {
  if (isExistingAccountsStep(currentRole, currentStep, schoolSyncStats))
    return 'new-users';

  return 'existing-users';
};

// returns true if there are existing accounts to review
export const hasExistingAccounts = function (
  currentRole: string,
  schoolSyncStats: SchoolSyncStatistics
) {
  let existingNotReviewed, existingSync, existingDoNotSync: number;
  if (currentRole === 'teacher') {
    existingNotReviewed = schoolSyncStats.totalExistingTeachersNotReviewed;
    existingSync = schoolSyncStats.totalExistingTeachersWithSyncToRecord;
    existingDoNotSync = schoolSyncStats.totalExistingTeachersWithDoNotSync;
  } else {
    existingNotReviewed = schoolSyncStats.totalExistingStudentsNotReviewed;
    existingSync = schoolSyncStats.totalExistingStudentsWithSyncToRecord;
    existingDoNotSync = schoolSyncStats.totalExistingStudentsWithDoNotSync;
  }

  return existingNotReviewed + existingSync + existingDoNotSync > 0;
};

// returns true if there are *new* accounts to review
export const hasNewAccounts = function (
  currentRole: string,
  schoolSyncStats: SchoolSyncStatistics
) {
  let newNotReviewed, newSync, newDoNotSync: number;
  if (currentRole === 'teacher') {
    newNotReviewed = schoolSyncStats.totalNewTeachersNotReviewed;
    newSync = schoolSyncStats.totalNewTeachersWithCreateNewRecord;
    newDoNotSync = schoolSyncStats.totalNewTeachersWithDoNotSync;
  } else {
    newNotReviewed = schoolSyncStats.totalNewStudentsNotReviewed;
    newSync = schoolSyncStats.totalNewStudentsWithCreateNewRecord;
    newDoNotSync = schoolSyncStats.totalNewStudentsWithDoNotSync;
  }

  return newNotReviewed + newSync + newDoNotSync > 0;
};
